import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  DoCheck,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, Observable, of, isObservable } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  switchMap,
  startWith,
  catchError,
  tap,
} from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SharedModule } from 'src/app/shared/shared.module';
import { TrimInputDirective } from 'src/app/shared/directives/trimEnd.directive';
import { TimeHhMmFormatDirective } from 'src/app/shared/directives/timeHhMmFormat.directive';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { CustomErrorTooltipComponent } from '../custom-error-tooltip/custom-error-tooltip.component';
import { FieldClearSuffixComponent } from '../field-clear-suffix/field-clear-suffix.component';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  FieldConfig,
  FieldChangeEvent,
  FieldClickEvent,
  InputSubType,
  OptionItem,
} from './field-config.interface';
import { ActionIcons, validationErrorMessages } from 'src/app/shared/utility/constants';

/**
 * FieldRendererComponent
 *
 * A configurable form field renderer supporting text display, inputs, selects, and autocomplete.
 *
 * @example Basic text display
 * ```html
 * <app-field-renderer [config]="{ key: 'name', type: 'text' }" [row]="data" />
 * ```
 *
 * @example Input with validation
 * ```html
 * <app-field-renderer
 *   [config]="{ key: 'code', type: 'input', inputType: 'alphanumeric', maxLength: 10 }"
 *   [control]="form.get('code')"
 *   [row]="data"
 *   (valueChange)="onFieldChange($event)"
 * />
 * ```
 *
 * @example Select dropdown
 * ```html
 * <app-field-renderer
 *   [config]="{
 *     key: 'status',
 *     type: 'select',
 *     options: statusOptions,
 *     optionDisplayField: 'label',
 *     optionValueField: 'value'
 *   }"
 *   [control]="form.get('status')"
 *   [row]="data"
 * />
 * ```
 *
 * @example Autocomplete with search
 * ```html
 * <app-field-renderer
 *   [config]="{
 *     key: 'city',
 *     type: 'autocomplete',
 *     onSearch: searchCities,
 *     minSearchLength: 2
 *   }"
 *   [control]="form.get('city')"
 *   [row]="data"
 * />
 * ```
 */
@Component({
  selector: 'app-field-renderer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    SharedModule,
    TrimInputDirective,
    TimeHhMmFormatDirective,
    SvgIconComponent,
    CustomErrorTooltipComponent,
    FieldClearSuffixComponent,
  ],
  templateUrl: './field-renderer.component.html',
  styleUrls: ['./field-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldRendererComponent<
    TRow = unknown,
    TOption extends OptionItem = OptionItem,
  >
  implements OnInit, OnDestroy, OnChanges, DoCheck
{
  @Input() config!: FieldConfig<TRow, TOption>;
  @Input() control?: FormControl;
  @Input() formGroup?: FormGroup;
  @Input() row!: TRow;
  @Input() rowIndex = 0;

  @Output() valueChange = new EventEmitter<FieldChangeEvent<TRow>>();
  @Output() blurChangeEvent = new EventEmitter<FieldChangeEvent<TRow>>();
  @Output() linkClick = new EventEmitter<FieldClickEvent<TRow>>();
  public actionIcons = ActionIcons;
  public autocompleteOptions$!: Observable<TOption[]>;
  public showErrorTooltip = false;
  public tooltipWidth = '0px';

  @ViewChild('autocompleteTrigger')
  private autocompleteTrigger?: MatAutocompleteTrigger;

  private readonly destroy$ = new Subject<void>();
  private readonly searchTerm$ = new Subject<string>();
  private previousTouched = false;
  private pendingAutoSelectPanelClose = false;

  constructor(
    public commonService: CommonService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateDisabledState();

    if (this.config.type === 'autocomplete') {
      this.setupAutocomplete();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row'] || changes['config']) {
      this.updateDisabledState();
    }
  }

  ngDoCheck(): void {
    // Detect when control's touched state changes (e.g., after markAllAsTouched())
    // This is needed because OnPush doesn't detect internal control state changes
    const currentTouched = this.control?.touched ?? false;
    if (currentTouched !== this.previousTouched) {
      this.previousTouched = currentTouched;
      this.cdRef.markForCheck();
    }
  }

  private updateDisabledState(): void {
    if (!this.control) return;

    const shouldBeDisabled = this.isDisabled;

    if (shouldBeDisabled && this.control.enabled) {
      this.control.disable();
    } else if (!shouldBeDisabled && this.control.disabled) {
      this.control.enable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasError(): boolean {
    if (!this.control?.invalid) {
      return false;
    }
    if (this.config.type === 'autocomplete') {
      return !!(this.control.dirty || this.control.touched);
    }
    return !!(this.control.touched && this.control.invalid);
  }

  get isDisabled(): boolean {
    if (this.config.disabledFn) {
      return this.config.disabledFn(this.row, this.formGroup);
    }
    return this.config.disabled ?? false;
  }

  get displayValue(): string {
    if (this.config.displayFn) {
      return this.config.displayFn(this.row);
    }
    const key = this.config.key as keyof TRow;
    const value = this.row?.[key];
    return value == null ? '' : String(value);
  }

  get optionDisplay(): string {
    return (this.config.optionDisplayField as string) ?? 'name';
  }

  get optionValue(): string {
    return (this.config.optionValueField as string) ?? 'name';
  }

  get resolvedOptions(): TOption[] {
    if (this.config.getOptions) {
      const opts = this.config.getOptions(this.row, this.rowIndex);
      return opts ?? [];
    }
    return this.config.options ?? [];
  }

  get maxDigits(): number {
    return this.config.maxDigits ?? 10;
  }

  get maxLength(): number {
    return this.config.maxLength ?? 100;
  }

  get shouldTrim(): boolean {
    return this.config.trim ?? this.inputSubType === 'basic';
  }

  get shouldUppercase(): boolean {
    return this.config.uppercase ?? false;
  }

  get inputSubType(): InputSubType {
    if (this.config.dynamicInputType && this.formGroup) {
      return this.config.dynamicInputType(this.row, this.formGroup);
    }
    return this.config.inputType ?? 'basic';
  }

  get shouldShowHyperlink(): boolean {
    if (!this.config.isHyperlink) return false;
    if (this.config.hyperlinkCondition) {
      return this.config.hyperlinkCondition(this.row);
    }
    return true;
  }

  get showInfoIcon(): boolean {
    return this.config.showInfoTooltip === true && !this.hasError;
  }

  get infoTooltipText(): string {
    return this.config.infoTooltipText ?? '';
  }

  get infoTooltipPosition(): 'above' | 'below' | 'left' | 'right' {
    return this.config.infoTooltipPosition ?? 'above';
  }

  private setupAutocomplete(): void {
    const debounce = this.config.debounceTime ?? 300;
    const minLength = this.config.minSearchLength ?? 0;

    let initialOptions$: Observable<TOption[]>;
    if (this.config.initialOptions) {
      if (isObservable(this.config.initialOptions)) {
        initialOptions$ = this.config.initialOptions;
      } else {
        initialOptions$ = of(this.config.initialOptions);
      }
    } else {
      initialOptions$ = of([]);
    }

    this.autocompleteOptions$ = this.searchTerm$.pipe(
      startWith(''),
      debounceTime(debounce),
      switchMap(term => {
        if (term === '' && this.config.initialOptions) {
          return initialOptions$;
        }
        if (term.length < minLength) {
          return initialOptions$;
        }
        if (this.config.onSearch) {
          return this.config
            .onSearch(term)
            .pipe(catchError(() => of([] as TOption[])));
        }
        return of([] as TOption[]);
      }),
      tap(() => {
        if (this.pendingAutoSelectPanelClose) {
          this.pendingAutoSelectPanelClose = false;
          this.autocompleteTrigger?.closePanel();
          this.cdRef.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  onAutocompleteInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const autoSelectedValue = this.tryAutoSelectAutocomplete(value);
    if (autoSelectedValue !== null) {
      this.pendingAutoSelectPanelClose = true;
      this.searchTerm$.next(autoSelectedValue);
      this.autocompleteTrigger?.closePanel();
    } else {
      this.searchTerm$.next(value);
    }
  }

  private tryAutoSelectAutocomplete(inputValue: string): string | null {
    if (!this.formGroup) {
      return null;
    }

    const dropdownData = this.resolvedOptions;
    if (dropdownData.length) {
      const beforeValue = this.control?.value;
      const beforeInvalid = this.control?.invalid ?? false;
      this.commonService.getAutocompleteDropdownId(
        dropdownData,
        this.formGroup,
        this.config.key,
        this.config.apiControlName ?? ''
      );

      const afterValue = this.control?.value;
      if (afterValue !== beforeValue) {
        const selectedValue = String(afterValue ?? '');
        this.emitChange();
        if (this.config.onValueChange) {
          this.config.onValueChange(afterValue, this.rowIndex);
        }
        if (this.config.onSelectionChange) {
          this.config.onSelectionChange({ value: afterValue }, this.rowIndex);
        }
        this.cdRef.markForCheck();
        return selectedValue;
      }

      if ((this.control?.invalid ?? false) !== beforeInvalid) {
        this.emitChange();
      }
    }

    this.cdRef.markForCheck();
    return null;
  }

  onAutocompleteSelected(event: unknown): void {
    this.handleSelectionChange(event);
  }

  onInputChange(): void {
    this.emitChange();
    if (this.config.onValueChange) {
      this.config.onValueChange(this.control?.value, this.rowIndex);
    }
  }

  onInputBlur(): void {
    this.blurChangeEvent.emit({
      key: this.config.key,
      value: this.control?.value,
      row: this.row,
      rowIndex: this.rowIndex,
    });
    if (this.config.onBlur) {
      this.config.onBlur(this.control?.value, this.rowIndex);
    }
  }

  onSelectChange(event: unknown): void {
    this.handleSelectionChange(event);
  }

  onFieldCleared(): void {
    if (this.config.type === 'autocomplete') {
      this.searchTerm$.next('');
    }
    this.emitChange();
    if (this.config.onValueChange) {
      this.config.onValueChange(this.control?.value, this.rowIndex);
    }
    if (this.config.onSelectionChange) {
      this.config.onSelectionChange(
        { value: this.control?.value },
        this.rowIndex
      );
    }
    this.cdRef.markForCheck();
  }

  private handleSelectionChange(event: unknown): void {
    this.emitChange();
    if (this.config.onSelectionChange) {
      this.config.onSelectionChange(event, this.rowIndex);
    }
  }

  onLinkClick(): void {
    this.linkClick.emit({
      key: this.config.key,
      row: this.row,
      rowIndex: this.rowIndex,
    });
  }

  private emitChange(): void {
    this.valueChange.emit({
      key: this.config.key,
      value: this.control?.value,
      row: this.row,
      rowIndex: this.rowIndex,
    });
  }

  onErrorIconClick(event: MouseEvent): void {
    event.stopPropagation();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const iconWidth = rect.width || 24;
    this.tooltipWidth = `${Math.max(iconWidth, 200)}px`;
    this.showErrorTooltip = true;
    this.cdRef.markForCheck();
  }

  closeErrorTooltip(): void {
    this.showErrorTooltip = false;
    this.cdRef.markForCheck();
  }

  getErrorMessage(): string {
    if (!this.control?.errors) return '';

    const errorKey = Object.keys(this.control.errors)[0];
    if (this.config.errorMessages?.[errorKey]) {
      return this.config.errorMessages[errorKey];
    }

    const defaults: Record<string, string> = {
      required: 'This field is required',
      minlength: 'Value is too short',
      maxlength: 'Value is too long',
      pattern: 'Invalid format',
      min: 'Value is too small',
      max: 'Value is too large',
      autocompleteError: validationErrorMessages.noResultsFound,
    };

    return defaults[errorKey] ?? 'Invalid value';
  }
}
